// Component to handle choosing from previous address history

import React from 'react';
import { History } from 'lucide-react';
import ProgressDialog from '../ProgressDialog';
import Select from '../ui/Select';
import '../../styles/shared.css';

interface AddressHistory {
  id: string;
  pickup: string;
  destination: string;
}

interface AddressHistoryDropdownProps {
  addressHistory: AddressHistory[];
  isLoading: boolean;
  onSelect: (pickup: string, destination: string) => void;
  className?: string;
  disabled?: boolean;
}

const AddressHistoryDropdown: React.FC<AddressHistoryDropdownProps> = ({ 
  addressHistory, 
  isLoading, 
  onSelect,
  className = '',
  disabled = false,
}) => {
  const [selectedId, setSelectedId] = React.useState<string>('');

  if (isLoading) {
    return <ProgressDialog isOpen={isLoading} message="Loading address history..." subMessage="Please wait while we fetch your previous trips." />;
  }

  if (!addressHistory || addressHistory.length === 0) {
    return null;
  }

  // Prepare options for Select
  const options = [
    { value: '', label: 'Select a previous trip...', disabled: true },
    ...addressHistory.map(addr => ({
      value: addr.id,
      label: `${addr.pickup.substring(0, 30)}${addr.pickup.length > 30 ? '...' : ''} to ${addr.destination.substring(0, 30)}${addr.destination.length > 30 ? '...' : ''}`
    }))
  ];

  const handleSelectChange = (value: string) => {
    setSelectedId(value);
    const selectedTrip = addressHistory.find(addr => addr.id === value);
    if (selectedTrip) {
      onSelect(selectedTrip.pickup, selectedTrip.destination);
      // Reset dropdown after populating the fields
      setTimeout(() => setSelectedId(''), 100);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Select
        label="Previous Trips"
        options={options}
        value={selectedId}
        onChange={val => handleSelectChange(val as string)}
        leftIcon={<History className="form-input-icon-left" />}
        placeholder={undefined}
        disabled={disabled}
      />
    </div>
  );
};

export default AddressHistoryDropdown;
